import _ from 'lodash';
import { validate as uuidValidate } from 'uuid';

import {
  balanceCollection, getDocs, limit, orderBy, query, startAfter, subsidiaryCollection, where
} from '../config/firebase';
import api from '../config/functionsApi';
import balanceStatus from '../models/enums/balanceStatus';
import BalanceFirebase, { ClientPrepaidBalance } from '../models/interfaces/balance';
import subsidiaryService from './subsidiaryService';

const BALANCE_PAGE_SIZE = 500;

const toDate = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  return null;
};

const resolveSubsidiaryId = async (id: string | null | undefined): Promise<string | null | undefined> => {
  if (!id || !uuidValidate(id)) return id;
  const sub = await subsidiaryService.getByIdAsync(id);
  const snap = await getDocs(query(subsidiaryCollection, where('cnpj', '==', sub.cnpj)));
  if (snap.empty) return id;
  return snap.docs[0].id;
};

const fetchApprovedBalancesPage = async (
  subsidiaryId: string,
  cursor: any | null
): Promise<{ items: BalanceFirebase[]; nextCursor: any | null }> => {
  let q = query(
    balanceCollection,
    where('subsidiary', '==', subsidiaryId),
    orderBy('createdAt', 'desc'),
    limit(BALANCE_PAGE_SIZE)
  );
  if (cursor) {
    q = query(
      balanceCollection,
      where('subsidiary', '==', subsidiaryId),
      orderBy('createdAt', 'desc'),
      startAfter(cursor),
      limit(BALANCE_PAGE_SIZE)
    );
  }

  const snapshot = await getDocs(q);
  const items = snapshot.docs.map((snap: any) => {
    const model = snap.data() as BalanceFirebase;
    return {
      ...model,
      id: snap.id,
      approvedDate: toDate(snap.data().approvedDate),
      createdAt: toDate(snap.data().createdAt)
    };
  });

  const lastDoc = snapshot.docs.length === BALANCE_PAGE_SIZE
    ? snapshot.docs[snapshot.docs.length - 1]
    : null;

  return { items, nextCursor: lastDoc };
};

const fetchAllApprovedBalances = async (
  subsidiaryId: string,
  cursor: any | null = null,
  acc: BalanceFirebase[] = []
): Promise<BalanceFirebase[]> => {
  const { items, nextCursor } = await fetchApprovedBalancesPage(subsidiaryId, cursor);
  const approved = items.filter((item) => item.status === balanceStatus.Approved && item.owner?.id);
  const nextAcc = acc.concat(approved);
  if (!nextCursor) return nextAcc;
  return fetchAllApprovedBalances(subsidiaryId, nextCursor, nextAcc);
};

export default {
  async getPrePagoReportAsync(id: string | null | undefined, startDate: Date, endDate: Date, customerId: string | null): Promise<BalanceFirebase[]> {
    let subsidiaryId = id;
    if (subsidiaryId) {
      if (uuidValidate(subsidiaryId)) {
        const sub = await subsidiaryService.getByIdAsync(subsidiaryId);
        const snap = await getDocs(query(subsidiaryCollection, where('cnpj', '==', sub.cnpj)));
        if (!snap.empty) {
          subsidiaryId = snap.docs[0].id;
        }
      }
    }

    let q = query(balanceCollection, where('subsidiary', '==', subsidiaryId),
      where('createdAt', '>=', startDate),
      where('createdAt', '<=', endDate),
      orderBy('createdAt', 'desc'));
    if (customerId) {
      q = query(q, where('owner.id', '==', customerId));
    }

    const result = await getDocs(q).then(async (querySnapshot) => querySnapshot.docs.map((snap: any) => {
      const model = snap.data() as BalanceFirebase;

      return {
        ...model,
        id: snap.id,
        value: model.value / 100,
        approvedDate: snap.data().approvedDate?.toDate(),
        udpatedAt: snap.data().udpatedAt?.toDate(),
        createdAt: snap.data().createdAt?.toDate()
      };
    }));

    // const transactionsQuery = query(balanceTransactionsCollection,
    //   where('createdAt', '>=', startDate), where('createdAt', '<=', endDate),
    //   orderBy('createdAt', 'desc'));
    // const transactions = await getDocs(transactionsQuery).then(async (querySnapshot) => querySnapshot.docs.map((snapT: any) => {
    //   const modelT = snapT.data() as BalanceTransactionFirebase;
    //   const model = _.find(balanceList, (x) => x.id === modelT.id);

    //   return {
    //     approvedBy: model?.approvedBy ?? '?',
    //     approvedDate: model?.approvedDate ?? null,
    //     comments: model?.comments ?? null,
    //     currentBalance: model?.currentBalance ?? null,
    //     owner: model?.owner ?? null,
    //     payment: model?.payment ?? null,
    //     previousBalance: model?.previousBalance ?? null,
    //     receiptUrl: model?.receiptUrl ?? null,
    //     status: model?.status ?? null,
    //     subsidiary: model?.subsidiary ?? null,
    //     updatedAt: model?.updatedAt ?? null,
    //     id: modelT.id,
    //     type: modelT.type,
    //     value: modelT.value,
    //     createdAt: snapT.data().createdAt?.toDate()
    //   };
    // }));

    return result;
  },

  async approveAsync(id: string): Promise<BalanceFirebase> {
    return api.post('balance/approve', { balanceId: id }).then((response) => {
      if (!response.data.success) throw new Error('Serviço indisponível');

      return response.data.balance;
    }).catch((e) => {
      throw new Error(e.response?.data?.message ?? 'Ops, aconteceu algo inesperado, por favor tente novamente');
    });
  },

  async getCurrentBalanceBySubsidiaryAsync(id: string | null | undefined): Promise<ClientPrepaidBalance[]> {
    const subsidiaryId = await resolveSubsidiaryId(id);
    if (!subsidiaryId) return [];

    const approved = await fetchAllApprovedBalances(subsidiaryId);
    const latestByClient = new Map<string, BalanceFirebase>();

    approved.forEach((item) => {
      const clientId = item.owner.id;
      const current = latestByClient.get(clientId);
      const itemTime = (item.approvedDate ?? item.createdAt)?.getTime() ?? 0;
      const currentTime = (current?.approvedDate ?? current?.createdAt)?.getTime() ?? 0;
      if (!current || itemTime > currentTime) {
        latestByClient.set(clientId, item);
      }
    });

    const rows = Array.from(latestByClient.values()).map((item) => ({
      clientId: item.owner.id,
      clientName: item.owner.name || 'Cliente sem nome',
      phone: item.owner.phone,
      currentBalance: (item.currentBalance ?? 0) / 100,
      lastApprovedAt: item.approvedDate ?? item.createdAt
    }));

    return _.orderBy(rows, ['currentBalance', 'clientName'], ['desc', 'asc']);
  },

  async getCurrentBalanceAsync(customerId: string): Promise<number> {
    const q = query(balanceCollection, where('owner.id', '==', customerId),
      orderBy('approvedDate', 'desc'), limit(1));

    const result = await getDocs(q).then(async (querySnapshot) => querySnapshot.docs.map((snap: any) => {
      const model = snap.data() as BalanceFirebase;

      return {
        ...model,
        id: snap.id,
        value: model.value / 100,
        currentBalance: model.currentBalance / 100,
        approvedDate: snap.data().approvedDate?.toDate(),
        udpatedAt: snap.data().udpatedAt?.toDate(),
        createdAt: snap.data().createdAt?.toDate()
      };
    }));

    const data = result.filter((x) => x.status === balanceStatus.Approved);

    return _.first(data)?.currentBalance ?? 0;
  }
};
